import React, { useCallback, useEffect, useState } from 'react'

import { useTheme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { useMediaQuery, IconButton, Box, Tooltip } from '@mui/material'
import ApiTable from './ApiTable'
import ApiList from './ApiList'
import { Filters } from './Filters'
import { FilterType, InternalFilter } from './ApiViewModels'
import { SearchField } from '../InputFields'
import { Paper } from '../Paper'
import MenuIcon from '@mui/icons-material/Menu'
import { SortingConfirguration, TableCellPadding } from '../Table/types'
import { useListUrlParams } from '../../Hooks'
import { Reset } from '@e3dc-react/icons'
import { useCustomTranslation } from '../../Hooks/useCustomTranslation'
import { UrlParams } from '../../Types'

export interface ApiTableColumn<T> {
  name: keyof T
  label: string
  value: (item: T) => React.ReactNode
  sortable?: boolean
}

export interface ListProps<T> {
  primary?: (item: T) => React.ReactNode
  secondary?: (item: T) => React.ReactNode
  icon?: (item: T) => React.ReactNode
  dataQa?: (item: T) => string
}

export interface FetchProps {
  url: string
  connect?: string
  filter?: string
  orderBy?: string
  limit?: number
  offset?: number
  token?: string
  endpoint?: string
  searchPhrase?: string
}

export interface Filter<T> {
  name: keyof T
  label: string
  type: FilterType
  /** replaces built-in onChange for the current filter, uses for custom logic with value */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customOnChange?: (value: any) => void
  /** extends styles for current filter */
  className?: string
}

export interface ApiViewProps<T> {
  // filters
  filters?: Filter<T>[]
  hideFilters?: boolean
  // filter given properties with ONE inputFilterField
  oneFilter?: {
    properties: (keyof T)[]
    propertiesWithoutWildCards?: string[]
    label: string
    filterQuery: string
    setFilterQuery: React.Dispatch<React.SetStateAction<string>>
    // max length of the input field(user won't be able to type more than this)
    maxLength?: number
    // min length of the input field(request won't be sent if the search input value is shorter than this)
    minLength?: number
    customFilterValueTransformer?: (filterValue: string) => string
    urlParams?: UrlParams
  }
  fetchProps: FetchProps
  listProps?: ListProps<T>
  tableColumns: ApiTableColumn<T>[]
  customTableColumns?: ApiTableColumn<T>[]
  hasColoredTableColumns?: boolean
  // functions
  onSelect?: (item: T) => void
  onAfterFetch?: (data?: T[], count?: number) => void
  onSelectOnlyInList?: boolean
  customFunctionalities?: ((item: T) => React.ReactNode)[]
  // other settings
  alternativeIcon?: React.ReactElement
  alternativeView?: React.ReactElement
  withLoading?: boolean
  onlyTable?: boolean
  className?: string
  noData?: string
  title?: string
  bodyElevation?: number
  headerElevation?: number
  footerElevation?: number
  listElevation?: number
  dataQaPrefix?: string
  collapsibleContent?: (row: T) => React.ReactNode
  collapsibleContentPadding?: TableCellPadding
  containerClassName?: string
  headerClassName?: string
  muiTableClassName?: string
  filterClassName?: string
  bodyClassName?: string
  setCurrPage?: (value: number) => void
  currPage?: number
  scrollMarginTop?: number
  hasFloatingButton?: boolean
  hasBottomBar?: boolean
  isMobilePaginator?: boolean
  customFunctionalityClass?: string
  blankView?: JSX.Element
  hideListFab?: boolean
  initialTableSort?: SortingConfirguration<T>
  onTableSortChange?: (newSort: SortingConfirguration<T>) => void
  initialTablePageLimit?: number
  onTablePageLimitChange?: (newTablePageLimit: number) => void
  /** default: true */
  resetTablePageAfterDataChange?: boolean
  customPayload?: Record<string, unknown>
  rowsPerPageOptions?: number[]
  actionButton?: React.ReactNode
}

const useStyles = makeStyles()(theme => ({
  inputFilter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 0,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper2,
    borderRadius: theme.shape.borderRadius,
  },
  padding: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  iconButton: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transition: 'all 0.2s ease-in-out',

    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      transition: 'all 0.2s ease-in-out',
    },
  },
  multipleButtons: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
}))

/**
 * Filters and renames props, that are sent to ApiList or ApiTable.
 * e.g. the props listPrimary and listSecondary are filtered
 * The same goes for list: listPrimary -> primary, listSecondary -> secondary, table* -> remove(table*)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ApiView = <T extends { [key: string]: any }>({
  filters,
  hideFilters,
  fetchProps,
  listProps,
  tableColumns,
  customTableColumns,
  hasColoredTableColumns,
  onSelectOnlyInList,
  className,
  alternativeIcon,
  alternativeView,
  withLoading,
  onlyTable,
  noData,
  onSelect,
  oneFilter,
  customFunctionalities,
  title,
  bodyElevation,
  headerElevation,
  footerElevation,
  listElevation,
  collapsibleContent,
  collapsibleContentPadding,
  dataQaPrefix,
  onAfterFetch,
  containerClassName,
  headerClassName,
  muiTableClassName,
  filterClassName,
  bodyClassName,
  // pixels for margin top of the list if the list is shown and scroll to top of the list is clicked
  scrollMarginTop,
  // if the page has already a floating button, the button to scroll to top of the list will have bottom margin
  hasFloatingButton,
  hasBottomBar = true,
  setCurrPage,
  currPage,
  isMobilePaginator,
  customFunctionalityClass,
  blankView,
  hideListFab = false,
  initialTableSort,
  onTableSortChange,
  initialTablePageLimit = 10,
  onTablePageLimitChange,
  resetTablePageAfterDataChange = true,
  customPayload,
  rowsPerPageOptions,
  actionButton,
}: ApiViewProps<T>): React.ReactElement => {
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const { t } = useCustomTranslation()
  const [totalRecordsCount, setTotalRecordsCount] = useState<number>(0)
  const { updateUrlFilter, updateUrlPage, urlPage, urlSort, updateUrlSort, urlFilter, urlLimit, updateUrlLimit, resetFilters, searchFieldMaxLength } =
    useListUrlParams<T>({
      urlParams: {
        ...oneFilter?.urlParams,
        enabled: oneFilter?.urlParams?.enabled ?? false,
      },
      setCurrentPage: setCurrPage,
      tableColumns,
      initialTablePageLimit,
      initialTableSort,
      fetchProps,
      totalRecordsCount,
    })

  const fullscreen = useMediaQuery(theme.breakpoints.down('lg'))
  const [internalFilters, setInternalFilters] = useState<InternalFilter[]>([])
  // filter string set from filters component
  const [filterString, setFilterString] = useState('')
  const [showAlternative, setShowAlternative] = useState<boolean>(false)

  useEffect(() => {
    changeFilter(urlFilter ?? '')
  }, [urlFilter]) //eslint-disable-line

  useEffect(() => {
    updateUrlLimit(urlLimit)
  }, [urlLimit]) //eslint-disable-line

  useEffect(() => {
    updateUrlPage(urlPage ?? 0)
  }, [urlPage]) //eslint-disable-line

  const changeSort = (newSort: SortingConfirguration<T>): void => {
    updateUrlSort(newSort)
    onTableSortChange?.(newSort)
  }

  /**
   * This function is responsible of adding Asterisks to the filter to match the
   * BE requirements for Wildcards
   */
  const changeFilter = (value: string): void => {
    if (value && oneFilter) {
      const { customFilterValueTransformer, propertiesWithoutWildCards, properties, setFilterQuery, minLength } = oneFilter
      const withWildcard: string[] = []
      const withoutWildcard: string[] = []

      const transformedValue = customFilterValueTransformer ? customFilterValueTransformer(value) : value

      /** Finds any number of sequential spaces and replacing them with a single space instead. */
      const trimmedValue = transformedValue.replace(/\s+/g, ' ').trim()

      if (minLength && trimmedValue.length < minLength) {
        oneFilter.setFilterQuery('')
        updateUrlFilter('')
        return
      }

      updateUrlFilter(value)

      properties.forEach(property => {
        if (propertiesWithoutWildCards?.includes(String(property))) {
          withoutWildcard.push(`${String(property)}=${trimmedValue}`)
        } else {
          withWildcard.push(`${String(property)}=*${trimmedValue}*`)
        }
      })

      setFilterQuery([...withWildcard, ...withoutWildcard].join(','))
    } else if (oneFilter) {
      oneFilter.setFilterQuery('')
      updateUrlFilter('')
    }
  }

  const onSetCurrentPage = useCallback(
    (page: number) => {
      updateUrlPage(page)
      setCurrPage?.(page)
    },
    [updateUrlPage, setCurrPage]
  )

  const onSetPageLimit = (newLimit: number): void => {
    updateUrlLimit(newLimit)
    onTablePageLimitChange?.(newLimit)
  }

  const onAfterFetchCallback = (data?: T[], count?: number): void => {
    setTotalRecordsCount(count ?? 0)
    onAfterFetch?.(data, count)
  }

  const renderSearchField = (): React.ReactElement => {
    if (oneFilter && oneFilter.properties.length > 0) {
      return (
        <div
          className={cx(filterClassName ?? classes.inputFilter, {
            [classes.multipleButtons]: !!oneFilter?.urlParams?.enabled && !!alternativeView,
          })}
        >
          <SearchField
            label={oneFilter.label + '...'}
            onChange={value => {
              changeFilter(value)
            }}
            value={urlFilter}
            maxLength={oneFilter.maxLength ?? searchFieldMaxLength}
            data-qa="search-field"
          />
          {(oneFilter?.urlParams?.enabled || alternativeView || actionButton) && (
            <Box display="flex" alignItems="center" ml={2} gap={2}>
              {actionButton}
              {oneFilter?.urlParams?.enabled && (
                <Tooltip title={t('common.reset-table-status')}>
                  <IconButton className={classes.iconButton} onClick={() => resetFilters(initialTablePageLimit)}>
                    <Reset />
                  </IconButton>
                </Tooltip>
              )}
              {alternativeView && (
                <IconButton
                  className={classes.iconButton}
                  onClick={() => {
                    setShowAlternative(!showAlternative)
                  }}
                >
                  {showAlternative ? <MenuIcon /> : alternativeIcon}
                </IconButton>
              )}
            </Box>
          )}
        </div>
      )
    } else {
      return <></>
    }
  }

  const renderFilter = (): React.ReactElement => {
    if (filters && filters.length > 0 && !hideFilters) {
      return (
        <Paper title={title ?? ''} className={classes.padding}>
          <Filters filters={filters} setFilterString={setFilterString} setInternalFilters={setInternalFilters} />
        </Paper>
      )
    } else {
      return <></>
    }
  }

  const renderView = (): React.ReactElement => {
    if (fullscreen && !onlyTable && listProps) {
      return (
        <ApiList<T>
          fetchProps={fetchProps}
          listProps={listProps}
          noData={noData}
          customFunctionalities={customFunctionalities}
          withLoading={withLoading}
          onSelect={onSelect}
          filterString={filterString}
          oneFilterString={oneFilter?.filterQuery}
          internalFilters={internalFilters}
          collapsibleContent={collapsibleContent}
          elevation={listElevation}
          dataQaPrefix={dataQaPrefix}
          onAfterFetch={onAfterFetchCallback}
          scrollMarginTop={scrollMarginTop}
          hasFloatingButton={hasFloatingButton}
          hasBottomBar={hasBottomBar}
          hideFab={hideListFab}
          initialPageLimit={urlLimit ?? initialTablePageLimit}
        />
      )
    } else if (showAlternative && alternativeView) {
      return alternativeView
    } else {
      return (
        <ApiTable<T>
          fetchProps={fetchProps}
          columns={tableColumns}
          customColumns={customTableColumns}
          hasColoredTableColumns={hasColoredTableColumns}
          noData={noData}
          customFunctionalities={customFunctionalities}
          withLoading={withLoading}
          onSelect={onSelectOnlyInList ? undefined : onSelect}
          filterString={filterString}
          internalFilters={internalFilters}
          oneFilterString={oneFilter?.filterQuery}
          bodyElevation={bodyElevation}
          headerElevation={headerElevation}
          footerElevation={footerElevation}
          collapsibleContent={collapsibleContent}
          collapsibleContentPadding={collapsibleContentPadding}
          onAfterFetch={onAfterFetchCallback}
          headerClassName={headerClassName}
          muiTableClassName={muiTableClassName}
          bodyClassName={bodyClassName}
          containerClassName={containerClassName}
          setCurrPage={onSetCurrentPage}
          currPage={urlPage ?? currPage}
          isMobilePaginator={isMobilePaginator}
          customFunctionalityClass={customFunctionalityClass}
          blankView={blankView}
          initialSort={urlSort ?? initialTableSort}
          onSortChange={changeSort}
          initialPageLimit={urlLimit ?? initialTablePageLimit}
          onPageLimitChange={onSetPageLimit}
          resetPageAfterDataChange={resetTablePageAfterDataChange && !oneFilter?.urlParams?.enabled}
          customPayload={customPayload}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )
    }
  }

  return (
    <>
      {renderSearchField()}
      {renderFilter()}
      {renderView()}
    </>
  )
}

export default ApiView
